import type { PAGE_POSITIONS } from 'types';

import StarFull from '~/assets/icons/StarFull';
import AppleAppStoreLogo from '~/assets/img/apple-app-store.svg';
import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import useRegion from '~/hooks/useRegion';

type Props = {
  downloadLink: string;
  pagePosition: PAGE_POSITIONS;
  qrCode: string;
};

export default function DownloadAppBlock({
  pagePosition,
  qrCode,
  downloadLink
}: Props) {
  const region = useRegion();

  if (region.code !== 'GB') return null;

  return (
    <section className="bg-dark-mode-black px-2 pt-8 md:px-8">
      <div className="mx-auto flex max-w-desktop flex-col items-center justify-center gap-8 md:flex-row lg:gap-20">
        <DisplayCopy
          pagePosition={pagePosition}
          qrCode={qrCode}
          downloadLink={downloadLink}
        />
        <AppDownloadDisplayImage />
      </div>
    </section>
  );
}

export function AppDownloadDisplayImage() {
  return (
    <div className="w-4/5">
      <Image
        alt="Get release reminders with The Sole Supplier iOS app"
        src="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/app_release_notification.png"
        loading="lazy"
      />
    </div>
  );
}

function DisplayCopy({ pagePosition, qrCode, downloadLink }: Props) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 max-md:px-2">
      <TitleAndDescription />

      <div className="flex w-full items-stretch justify-center gap-4 md:justify-start">
        <QrCode qrCode={qrCode} />
        <RatingsBlock pagePosition={pagePosition} downloadLink={downloadLink} />
      </div>
    </div>
  );
}

type StarsProps = {
  height?: string;
  width?: string;
};

export function Stars({ height = '24', width = '24' }: StarsProps) {
  return (
    <div className="flex gap-0.5">
      {new Array(5).fill(0).map((_, index) => (
        <div key={`star-rating-${index}`}>
          <span className="hidden lg:block">
            <StarFull fill="#FA0" height={height} width={width} />
          </span>
          <span className="lg:hidden">
            <StarFull fill="#FA0" height="16" width="16" />
          </span>
        </div>
      ))}
    </div>
  );
}

function RatingsBlock({
  pagePosition,
  downloadLink
}: {
  downloadLink: string;
  pagePosition: PAGE_POSITIONS;
}) {
  return (
    <div className="flex w-full items-center justify-center gap-2 rounded bg-dark-background px-6 py-4 text-center text-white md:w-fit md:flex-col md:gap-4">
      <DownloadFromAppStoreLink
        href={downloadLink}
        pagePosition={pagePosition}
      />

      <div className="flex items-center justify-center gap-2">
        <Stars />
        <span>4.8</span>
      </div>
    </div>
  );
}

function TitleAndDescription() {
  return (
    <div className="flex flex-col items-start justify-center gap-4 ">
      <div className="flex flex-col gap-2">
        <p className="text-title-xl text-golden-syrup">
          Tired of missing drops?
        </p>
        <p className="text-title-xl text-white">Get the app.</p>
      </div>
      <p className="leading-8 text-white">
        Up your sneaker game with our app. Receive instant, personalised release
        alerts from 50+ retailers, and stay in the loop with the latest sneakers
        and streetwear news. Boost your chances of copping by downloading our
        app now.
      </p>
    </div>
  );
}

function QrCode({ qrCode }: { qrCode: string }) {
  return (
    <div className="hidden aspect-square w-[120px] lg:block">
      <Image loading="lazy" alt="QR Code to download the app" src={qrCode} />
    </div>
  );
}

export function DownloadFromAppStoreLink({
  href,
  pagePosition
}: {
  href: string;
  pagePosition: PAGE_POSITIONS;
}) {
  return (
    <Tracked.ExternalLink
      name="DOWNLOAD_THE_APP"
      pagePosition={pagePosition}
      href={href}
      target="_blank"
      rel="noreferrer"
      variant="none"
    >
      <img
        alt="Download on the App Store"
        src={AppleAppStoreLogo}
        loading="lazy"
      />
    </Tracked.ExternalLink>
  );
}
